'use client';
/**
 * COHÉRENCE DU MODÈLE — Étape 5 de `SPEC-MODELE-DONNEES.md` (02/09/2026).
 *
 * ── Ce que cette page est ──────────────────────────────────────────────────
 *
 * « Un livrable de maîtrise documentaire, pas un outil de mise au point : elle
 * doit être consultable par la RQ, et son contenu doit pouvoir être versé au
 * dossier d'audit. »
 *
 * Elle rassemble ce que personne ne peut voir depuis un seul écran : un
 * processus dont le pilote est parti, des initiales portées à deux, une
 * personne planifiée sur un poste qu'elle ne tient pas. Chacun de ces faits est
 * visible quelque part — aucun n'est visible ENSEMBLE, et c'est là qu'ils
 * passent inaperçus.
 *
 * ⚠️ ELLE N'ÉCRIT RIEN, et c'est délibéré. Corriger depuis la page de contrôle
 * ferait d'elle une seconde porte d'écriture sur six registres — exactement ce
 * que la règle fondatrice interdit. Chaque constat NOMME l'écran où corriger.
 *
 * ⚠️ ZÉRO CONSTAT EST UNE AFFIRMATION. Servie avant l'arrivée des données, la
 * page dirait « le modèle est cohérent » alors qu'on n'a rien lu. Elle attend
 * donc sa réponse avant d'affirmer quoi que ce soit (relevé BLOQ-03).
 */
import React from 'react';
import { useGed } from '@/lib/store';
import { TitrePage, card, I, Skeleton, EmptyState, RefISO } from '@/components/ui';

import { Bouton } from '@/shared/Bouton';
type Gravite = 'bloquant' | 'majeur' | 'mineur';
interface Constat { cle: string; regle: string; gravite: Gravite; quoi: string; ou: string }

const TONS: Record<Gravite, { label: string; bg: string; fg: string }> = {
  bloquant: { label: 'Bloquant', bg: 'var(--red-50)', fg: 'var(--red-600)' },
  majeur: { label: 'Majeur', bg: 'var(--amber-50)', fg: 'var(--amber-600)' },
  /* ⚠️ Le mineur reste NEUTRE, jamais vert : vert dirait « conforme », et un
     écart mineur reste un écart. */
  mineur: { label: 'Mineur', bg: 'var(--gray-50)', fg: 'var(--text-muted)' },
};

export function Coherence() {
  const { go } = useGed();
  const [etat, setEtat] = React.useState<{
    constats: Constat[]; bilan: { total: number; bloquant: number; majeur: number; mineur: number };
    arreteAu: string;
  } | null>(null);
  const [erreur, setErreur] = React.useState('');
  const [filtre, setFiltre] = React.useState<Gravite | 'toutes'>('toutes');

  const charger = React.useCallback(async () => {
    setErreur('');
    try {
      const r = await fetch('/api/ged/ged/coherence', { credentials: 'include' });
      const j = await r.json().catch(() => null);
      if (!r.ok || !j?.ok) { setErreur(j?.error?.message || 'Le contrôle n’a pas pu être joué.'); return; }
      setEtat({ constats: j.constats || [], bilan: j.bilan, arreteAu: String(j.arreteAu || '') });
    } catch { setErreur('Le service est injoignable. Rien n’est perdu : réessayez.'); }
  }, []);
  // eslint-disable-next-line react-hooks/set-state-in-effect
  React.useEffect(() => { void charger(); }, [charger]);

  const vus = etat ? etat.constats.filter((c) => filtre === 'toutes' || c.gravite === filtre) : [];
  /* Groupés PAR RÈGLE : douze initiales en doublon sont un seul sujet, pas
     douze. Une liste plate de constats se lit comme une liste de reproches. */
  const parRegle = React.useMemo(() => {
    const m = new Map<string, Constat[]>();
    for (const c of vus) m.set(c.regle, [...(m.get(c.regle) || []), c]);
    return [...m.entries()];
  }, [vus]);

  return (
    <section>
      <style>{`@media print {
        @page { size: A4 portrait; margin: 12mm; }
        body * { visibility: hidden; }
        .coherence-ecran, .coherence-ecran * { visibility: visible; }
        .coherence-ecran { position: absolute; left: 0; top: 0; width: 100%; }
        .ged-btn { display: none !important; }
      }`}</style>
      <div className="coherence-ecran">
        <TitrePage eyebrow="Pilotage & revue" titre="Cohérence du modèle"
          sousTitre={<>
            Les endroits où le système dit deux choses, ou ne dit rien là où il devrait. Cette page
            ne corrige rien : chaque constat nomme l’écran où le faire. Elle s’imprime et se verse au
            dossier d’audit. <RefISO code="8.8.3" />
          </>}
          actions={
            <Bouton taille="sm" type="button" onClick={() => void charger()}>
              <I n="refresh" s={13} /> Rejouer le contrôle
            </Bouton>
          } />

        {erreur && (
          <div role="alert" style={{ ...card, padding: 14, marginTop: 14, borderInlineStart: '3px solid var(--red-600)' }}>
            <strong style={{ fontSize: 12.5, color: 'var(--red-600)' }}>{erreur}</strong>
          </div>
        )}

        {/* ⚠️ RIEN N'EST AFFIRMÉ TANT QU'ON N'A PAS LU. Un « aucun écart » servi
            pendant le chargement est le pire message de cette page : il rassure
            sur ce qu'on n'a pas vérifié. */}
        {!etat && !erreur && <div style={{ marginTop: 16 }}><Skeleton rows={5} height={44} /></div>}

        {etat && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, margin: '16px 0' }}>
              {([['toutes', 'Tous les constats', etat.bilan.total, false] as const,
                ['bloquant', 'Bloquants', etat.bilan.bloquant, true] as const,
                ['majeur', 'Majeurs', etat.bilan.majeur, false] as const,
                ['mineur', 'Mineurs', etat.bilan.mineur, false] as const,
              ]).map(([k, l, n, rouge]) => (
                <button key={k} type="button" className="ged-btn" aria-pressed={filtre === k}
                  onClick={() => setFiltre(filtre === k ? 'toutes' : (k as Gravite))}
                  style={{
                    ...card, padding: '13px 15px', textAlign: 'start', cursor: 'pointer',
                    font: 'inherit', fontFamily: 'var(--font-sans)',
                    border: `1.5px solid ${filtre === k ? 'var(--color-primary)' : 'var(--border-default)'}`,
                  }}>
                  <div style={{ fontSize: 23, fontWeight: 700, color: rouge && n > 0 ? 'var(--red-600)' : 'var(--navy-900)' }}>{n}</div>
                  <div style={{ fontSize: 12.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text-muted)', marginTop: 2 }}>{l}</div>
                </button>
              ))}
            </div>

            <p style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.6, maxWidth: '84ch', margin: '0 0 16px' }}>
              Contrôle arrêté au <strong>{etat.arreteAu}</strong>. Les affectations regardées sont
              celles <strong>à venir</strong> : celles du passé disent ce qui a eu lieu, et le
              reprocher au système serait lui reprocher d’avoir de la mémoire. Une{' '}
              <strong>dérogation</strong> n’est jamais un écart — elle est une décision motivée et
              journalisée.
            </p>

            {!vus.length ? (
              <EmptyState
                titre={filtre === 'toutes' ? 'Aucun écart de cohérence' : 'Aucun constat de cette gravité'}
                indice={filtre === 'toutes'
                  ? 'Chaque processus a son pilote, chaque personne en service sa fonction et des initiales à elle, et personne n’est planifié sur un poste qu’il ne tient pas.'
                  : 'Retirez le filtre pour voir les autres.'} />
            ) : parRegle.map(([regle, liste]) => (
              <section key={regle} style={{ ...card, padding: 0, marginBottom: 12, overflow: 'hidden' }}>
                <div style={{ padding: '10px 14px', background: 'var(--gray-25)', borderBottom: '1px solid var(--border-default)', display: 'flex', alignItems: 'center', gap: 10 }}>
                  <strong style={{ fontSize: 13 }}>{regle}</strong>
                  <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                    {liste.length} constat{liste.length > 1 ? 's' : ''}
                  </span>
                </div>
                {liste.map((c) => {
                  const ton = TONS[c.gravite];
                  return (
                    <div key={c.cle} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 14px', borderTop: '1px solid var(--border-subtle)' }}>
                      <span style={{
                        flex: 'none', fontSize: 10.5, fontWeight: 700, borderRadius: 'var(--radius-pill)',
                        padding: '3px 9px', background: ton.bg, color: ton.fg, marginTop: 1,
                      }}>{ton.label}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.55 }}>{c.quoi}</div>
                        {/* ⚠️ OÙ CORRIGER, sur chaque ligne. Un écart qu'on ne
                            sait pas réparer ne se corrige jamais, il se
                            contemple — et la page devient un mur de reproches. */}
                        <div style={{ fontSize: 12.5, color: 'var(--text-faint)', marginTop: 3 }}>
                          À corriger dans : <strong>{c.ou}</strong>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </section>
            ))}

            <div className="ged-btn" style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <Bouton taille="sm" type="button" onClick={() => window.print()}>
                <I n="printer" s={13} /> Imprimer pour le dossier d’audit
              </Bouton>
              <Bouton taille="sm" type="button" onClick={() => go('iso')}>
                Correspondance ISO →
              </Bouton>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
